/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ClinicalCustomerNamePageResponse } from '../models/clinical-customer-name-page-response';
import { ClinicalDepartmentEnum } from '../models/clinical-department-enum';

/**
 * Customer name catalog CRUD for FW Flight Request and Fixed Wing Chart Review
 */
@Injectable({ providedIn: 'root' })
export class ClinicalCustomerNamesService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `listAllClinicalCustomerNames()` */
  static readonly ListAllClinicalCustomerNamesPath =
    '/api/v1/clinical/customer-names';

  /**
   * List customer names across supported catalogs.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listAllClinicalCustomerNames()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllClinicalCustomerNames$Response(
    params?: {
      page?: number;
      size?: number;
      search?: string;

      /**
       * Field and direction, formatted as 'field,asc' or 'field,desc'. Supported fields: name, formType, active, createdAt. Default: name,asc
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalCustomerNamePageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalCustomerNamesService.ListAllClinicalCustomerNamesPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('search', params.search, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalCustomerNamePageResponse>;
        })
      );
  }

  /**
   * List customer names across supported catalogs.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listAllClinicalCustomerNames$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllClinicalCustomerNames(
    params?: {
      page?: number;
      size?: number;
      search?: string;

      /**
       * Field and direction, formatted as 'field,asc' or 'field,desc'. Supported fields: name, formType, active, createdAt. Default: name,asc
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<ClinicalCustomerNamePageResponse> {
    return this.listAllClinicalCustomerNames$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalCustomerNamePageResponse>
        ): ClinicalCustomerNamePageResponse => r.body
      )
    );
  }

  /** Path part for operation `listClinicalCustomerNames()` */
  static readonly ListClinicalCustomerNamesPath =
    '/api/v1/clinical/{department}/forms/{formType}/customer-names';

  /**
   * List customer names for a supported form.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClinicalCustomerNames()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalCustomerNames$Response(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      search?: string;

      /**
       * Field and direction, formatted as 'field,asc' or 'field,desc'. Supported fields: name, formType, active, createdAt. Default: name,asc
       */
      sort?: string;
      page?: number;
      size?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalCustomerNamesService.ListClinicalCustomerNamesPath,
      'get'
    );
    if (params) {
      rb.path('department', params.department, {});
      rb.path('formType', params.formType, {});
      rb.query('search', params.search, {});
      rb.query('sort', params.sort, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
    }

    return this.http
      .request(rb.build({ responseType: 'text', accept: '*/*', context }))
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * List customer names for a supported form.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listClinicalCustomerNames$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalCustomerNames(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      search?: string;

      /**
       * Field and direction, formatted as 'field,asc' or 'field,desc'. Supported fields: name, formType, active, createdAt. Default: name,asc
       */
      sort?: string;
      page?: number;
      size?: number;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.listClinicalCustomerNames$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `createClinicalCustomerName()` */
  static readonly CreateClinicalCustomerNamePath =
    '/api/v1/clinical/{department}/forms/{formType}/customer-names';

  /**
   * Create a customer name.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClinicalCustomerName()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClinicalCustomerName$Response(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      body: {
        name: string;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalCustomerNamesService.CreateClinicalCustomerNamePath,
      'post'
    );
    if (params) {
      rb.path('department', params.department, {});
      rb.path('formType', params.formType, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(rb.build({ responseType: 'text', accept: '*/*', context }))
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Create a customer name.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createClinicalCustomerName$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClinicalCustomerName(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      body: {
        name: string;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.createClinicalCustomerName$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `renameClinicalCustomerName()` */
  static readonly RenameClinicalCustomerNamePath =
    '/api/v1/clinical/{department}/forms/{formType}/customer-names/{id}';

  /**
   * Rename a non-seeded unused customer name.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `renameClinicalCustomerName()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  renameClinicalCustomerName$Response(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      id: number;
      body: {
        name: string;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalCustomerNamesService.RenameClinicalCustomerNamePath,
      'put'
    );
    if (params) {
      rb.path('department', params.department, {});
      rb.path('formType', params.formType, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(rb.build({ responseType: 'text', accept: '*/*', context }))
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Rename a non-seeded unused customer name.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `renameClinicalCustomerName$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  renameClinicalCustomerName(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      id: number;
      body: {
        name: string;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.renameClinicalCustomerName$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `deleteClinicalCustomerName()` */
  static readonly DeleteClinicalCustomerNamePath =
    '/api/v1/clinical/{department}/forms/{formType}/customer-names/{id}';

  /**
   * Hard-delete a non-seeded unused customer name.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteClinicalCustomerName()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClinicalCustomerName$Response(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalCustomerNamesService.DeleteClinicalCustomerNamePath,
      'delete'
    );
    if (params) {
      rb.path('department', params.department, {});
      rb.path('formType', params.formType, {});
      rb.path('id', params.id, {});
    }

    return this.http
      .request(rb.build({ responseType: 'text', accept: '*/*', context }))
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Hard-delete a non-seeded unused customer name.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteClinicalCustomerName$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClinicalCustomerName(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      id: number;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteClinicalCustomerName$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `setClinicalCustomerNameActive()` */
  static readonly SetClinicalCustomerNameActivePath =
    '/api/v1/clinical/{department}/forms/{formType}/customer-names/{id}';

  /**
   * Activate or deactivate a customer name.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `setClinicalCustomerNameActive()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setClinicalCustomerNameActive$Response(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      id: number;
      body: {
        active: boolean;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalCustomerNamesService.SetClinicalCustomerNameActivePath,
      'patch'
    );
    if (params) {
      rb.path('department', params.department, {});
      rb.path('formType', params.formType, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(rb.build({ responseType: 'text', accept: '*/*', context }))
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Activate or deactivate a customer name.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `setClinicalCustomerNameActive$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setClinicalCustomerNameActive(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      id: number;
      body: {
        active: boolean;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.setClinicalCustomerNameActive$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }
}
